/**
 * DealInvoiceModal
 * Creates one or more escrow invoices for a deal.
 * Modes:
 *   single       — one payer, one payee, one invoice
 *   multi_payer  — N payers → same payee → N invoices
 *   multi_receiver — same payer → N payees → N invoices
 *
 * Generated invoices follow the Escrow flow defined in payments.py
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { PlusCircle, Trash2, Loader2, FileText, Users, User } from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

// ── Helpers ──────────────────────────────────────────────────────────────────

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'PKR', 'AED'];

function buildParticipants(deal, clients, currentUser) {
    const participants = [];

    // Deal owner (always present)
    if (deal?.owner_user_id) {
        participants.push({
            id: deal.owner_user_id,
            type: 'user',
            label: currentUser?._id === deal.owner_user_id
                ? `${currentUser?.name || 'Me'} (You — Deal Owner)`
                : `${deal.owner_name || 'Deal Owner'}`,
            isOwner: true,
        });
    }

    // Clients linked to the deal
    (clients || []).forEach((c) => {
        participants.push({
            id: String(c._id),
            type: 'client',
            label: `${c.name || c.contact_email || 'Client'} (Client)`,
            isOwner: false,
        });
    });

    return participants;
}

function emptyEntry() {
    return { id: '', type: '', amount: '', description: '' };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DealInvoiceModal({
  open, onClose, deal, clients, currentUser }) {
  const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Mode: 'single' | 'multi_payer' | 'multi_receiver'
    const [mode, setMode] = useState('single');
    const [currency, setCurrency] = useState('USD');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');

    // Single mode
    const [singlePayerId, setSinglePayerId] = useState('');
    const [singlePayeeId, setSinglePayeeId] = useState('');
    const [singleAmount, setSingleAmount] = useState('');
    const [singleDescription, setSingleDescription] = useState('');

    // Inline validation errors
    const [errors, setErrors] = useState({});
    const clearError = (key) => setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

    // Character limits
    const DESC_MAX = 150;
    const NOTES_MAX = 500;

    // Multi mode rows
    const [multiRows, setMultiRows] = useState([emptyEntry()]);
    // In multi_payer mode: rows = payers; payee is fixed → singlePayeeId
    // In multi_receiver mode: rows = payees; payer is fixed → singlePayerId

    const participants = useMemo(
        () => buildParticipants(deal, clients, currentUser),
        [deal, clients, currentUser]
    );

    // Multi-mode is only meaningful when there are 2+ clients
    // (need at least 3 participants: owner + 2 clients for multi-payer/receiver to make sense)
    const allowMultiMode = (clients || []).length >= 2;

    // If multi mode is active but tabs are hidden (e.g. clients changed), snap back to single
    React.useEffect(() => {
        if (!allowMultiMode && mode !== 'single') {
            setMode('single');
            setMultiRows([emptyEntry()]);
        }
    }, [allowMultiMode, mode]);

    // Running total for multi modes
    const multiTotal = useMemo(
        () => multiRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
        [multiRows]
    );

    const dealValue = parseFloat(deal?.value) || null;

    // ── Row helpers ───────────────────────────────────────────────────────────

    const addRow = () => setMultiRows((prev) => [...prev, emptyEntry()]);
    const removeRow = (idx) => setMultiRows((prev) => prev.filter((_, i) => i !== idx));
    const updateRow = (idx, field, value) => {
        setMultiRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
        // Clear multiRows error as soon as any row is updated
        clearError('multiRows');
    };

    // ── Mutation ──────────────────────────────────────────────────────────────

    const createInvoicesMutation = useMutation({
        mutationFn: (payload) => api.post('/payments/invoices/create', payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['deal-invoices', deal?._id]);
            queryClient.invalidateQueries(['deal', deal?._id]);
            toast.success(`${data.count} invoice${data.count !== 1 ? 's' : ''} created successfully!`);
            handleClose();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.detail || 'Failed to create invoices');
        },
    });

    // ── Validation & submit ───────────────────────────────────────────────────

    const handleSubmit = () => {
        const newErrors = {};
        const entries = [];

        if (mode === 'single') {
            if (!singlePayerId) newErrors.payer = 'Payer is required';
            if (!singlePayeeId) newErrors.payee = 'Payee is required';
            if (singlePayerId && singlePayeeId && singlePayerId === singlePayeeId)
                newErrors.payee = 'Payer and Payee cannot be the same person';
            if (!singleAmount || parseFloat(singleAmount) <= 0)
                newErrors.amount = 'A valid amount is required';
            if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

            const payer = participants.find((p) => p.id === singlePayerId);
            const payee = participants.find((p) => p.id === singlePayeeId);
            entries.push({
                payer_id: singlePayerId,
                payer_type: payer?.type || 'user',
                payee_id: singlePayeeId,
                payee_type: payee?.type || 'user',
                amount: parseFloat(singleAmount),
                currency,
                description: singleDescription || 'Deal escrow payment',
                due_date: dueDate || undefined,
                notes: notes || undefined,
            });
        } else if (mode === 'multi_payer') {
            if (!singlePayeeId) newErrors.payee = 'Payee is required';
            const validPayerRows = multiRows.filter(r => r.id && parseFloat(r.amount) > 0);
            if (validPayerRows.length === 0) newErrors.multiRows = 'At least one payer with a valid amount is required';
            if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
            for (const row of multiRows) {
                if (!row.id) return toast.error('Please select a Payer for each row');
                const amount = parseFloat(row.amount);
                if (!amount || amount <= 0) return toast.error('Each row must have a valid amount');
                if (row.id === singlePayeeId) return toast.error('A Payer cannot also be the Payee');
                const payer = participants.find((p) => p.id === row.id);
                const payee = participants.find((p) => p.id === singlePayeeId);
                entries.push({
                    payer_id: row.id,
                    payer_type: payer?.type || 'user',
                    payee_id: singlePayeeId,
                    payee_type: payee?.type || 'user',
                    amount,
                    currency,
                    description: row.description || 'Deal escrow payment',
                    due_date: dueDate || undefined,
                    notes: notes || undefined,
                });
            }
        } else if (mode === 'multi_receiver') {
            if (!singlePayerId) newErrors.payer = 'Payer is required';
            const validPayeeRows = multiRows.filter(r => r.id && parseFloat(r.amount) > 0);
            if (validPayeeRows.length === 0) newErrors.multiRows = 'At least one payee with a valid amount is required';
            if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
            for (const row of multiRows) {
                if (!row.id) return toast.error('Please select a Payee for each row');
                const amount = parseFloat(row.amount);
                if (!amount || amount <= 0) return toast.error('Each row must have a valid amount');
                if (row.id === singlePayerId) return toast.error('A Payee cannot also be the Payer');
                const payer = participants.find((p) => p.id === singlePayerId);
                const payee = participants.find((p) => p.id === row.id);
                entries.push({
                    payer_id: singlePayerId,
                    payer_type: payer?.type || 'user',
                    payee_id: row.id,
                    payee_type: payee?.type || 'user',
                    amount,
                    currency,
                    description: row.description || 'Deal escrow payment',
                    due_date: dueDate || undefined,
                    notes: notes || undefined,
                });
            }
        }

        setErrors({});
        createInvoicesMutation.mutate({
            deal_id: String(deal?._id),
            mode,
            entries,
        });
    };

    const handleClose = () => {
        setMode('single');
        setCurrency('USD');
        setDueDate('');
        setNotes('');
        setSinglePayerId('');
        setSinglePayeeId('');
        setSingleAmount('');
        setSingleDescription('');
        setMultiRows([emptyEntry()]);
        setErrors({});
        onClose();
    };

    const loading = createInvoicesMutation.isPending;

    // ── Participant select helper ─────────────────────────────────────────────

    const renderParticipantSelect = useCallback(({ value, onChange, placeholder, disabledIds = [] }) => (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder || 'Select participant'} />
            </SelectTrigger>
            <SelectContent>
                {participants.map((p) => {
                    const disabledInfo = disabledIds.find(d => d.id === p.id);
                    const isDisabled = !!disabledInfo;
                    return (
                        <SelectItem key={p.id} value={p.id} disabled={isDisabled}>
                            <div className="flex items-center gap-2">
                                {p.isOwner ? <User className="h-3.5 w-3.5 text-blue-500" /> : <Users className="h-3.5 w-3.5 text-emerald-500" />}
                                <span>
                                    {p.label} {isDisabled ? <span className="text-muted-foreground italic text-xs ml-1">({disabledInfo.reason})</span> : ''}
                                </span>
                            </div>
                        </SelectItem>
                    );
                })}
            </SelectContent>
        </Select>
    ), [participants]);

    // ── Mode tab button ───────────────────────────────────────────────────────

    const ModeTab = ({ value, label, icon: Icon }) => (
        <button
            type="button"
            onClick={() => {
                setMode(value);
                setMultiRows([emptyEntry()]);
                setErrors({});
                setSinglePayerId('');
                setSinglePayeeId('');
                setSingleAmount('');
                setSingleDescription('');
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
        ${mode === value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
        >
            <Icon className="h-3.5 w-3.5" />
            {label}
        </button>
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
                {/* ── Sticky Header ── */}
                <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-emerald-600" />
                        Create Escrow Invoice
                    </DialogTitle>
                    <DialogDescription>
                        Define who pays whom. Funds will be held in escrow until released by the payer.
                    </DialogDescription>
                </DialogHeader>

                {/* ── Scrollable Body ── */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="space-y-5">

                        {/* ── Mode selector ── */}
                        <div>
                            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
                                Invoice Mode
                            </Label>
                            <div className="flex gap-1.5 p-1 bg-muted rounded-lg w-fit">
                                <ModeTab value="single" label="Single" icon={User} />
                                {allowMultiMode && (
                                    <>
                                        <ModeTab value="multi_payer" label="Multi-Payer" icon={Users} />
                                        <ModeTab value="multi_receiver" label="Multi-Receiver" icon={Users} />
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">
                                {mode === 'single' && 'One payer funds the escrow, one payee receives on release.'}
                                {mode === 'multi_payer' && 'Multiple payers each pay a separate amount to the same payee. Generates one invoice per payer.'}
                                {mode === 'multi_receiver' && 'One payer splits payment across multiple payees. Generates one invoice per payee.'}
                            </p>
                        </div>

                        {/* ── Currency & Due Date ── */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label>Currency</Label>
                                <Select value={currency} onValueChange={setCurrency}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label>Due Date <span className="text-muted-foreground">(optional)</span></Label>
                                <Input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                        </div>

                        {/* ── SINGLE MODE ── */}
                        {mode === 'single' && (
                            <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label className="flex items-center gap-1">
                                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400" />
                                            Payer <span className="text-xs text-muted-foreground">(funds escrow)</span>
                                            <span className="text-red-500 ml-0.5">*</span>
                                        </Label>
                                        {renderParticipantSelect({
                                            value: singlePayerId,
                                            onChange: (v) => { setSinglePayerId(v); clearError('payer'); },
                                            disabledIds: singlePayeeId ? [{ id: singlePayeeId, reason: 'Already selected as Payee' }] : [],
                                            placeholder: 'Who pays?',
                                            hasError: !!errors.payer,
                                        })}
                                        {errors.payer && <p className="text-xs text-red-500 mt-1">{errors.payer}</p>}
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="flex items-center gap-1">
                                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                            Payee <span className="text-xs text-muted-foreground">(receives funds)</span>
                                            <span className="text-red-500 ml-0.5">*</span>
                                        </Label>
                                        {renderParticipantSelect({
                                            value: singlePayeeId,
                                            onChange: (v) => { setSinglePayeeId(v); clearError('payee'); },
                                            disabledIds: singlePayerId ? [{ id: singlePayerId, reason: 'Already selected as Payer' }] : [],
                                            placeholder: 'Who receives?',
                                            hasError: !!errors.payee,
                                        })}
                                        {errors.payee && <p className="text-xs text-red-500 mt-1">{errors.payee}</p>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label>
                                            Amount ({currency})
                                            <span className="text-red-500 ml-0.5">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={singleAmount}
                                            onChange={(e) => { setSingleAmount(e.target.value); clearError('amount'); }}
                                            className={errors.amount ? 'border-red-400 focus-visible:ring-red-400' : ''}
                                        />
                                        {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
                                    </div>
                                    <div className="space-y-1">
                                        <Label>
                                            Description
                                            <span className="text-muted-foreground text-xs ml-1">(optional)</span>
                                        </Label>
                                        <Input
                                            placeholder="e.g. Full project payment"
                                            value={singleDescription}
                                            maxLength={DESC_MAX}
                                            onChange={(e) => setSingleDescription(e.target.value)}
                                        />
                                        <p className={`text-xs text-right mt-0.5 ${singleDescription.length >= DESC_MAX ? 'text-red-500' : 'text-muted-foreground'}`}>
                                            {singleDescription.length}/{DESC_MAX}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── MULTI-PAYER MODE ── */}
                        {mode === 'multi_payer' && (
                            <div className="space-y-4">
                                {/* Fixed Payee */}
                                <div className="space-y-1">
                                    <Label className="flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        Payee (receives all payments)
                                        <span className="text-red-500 ml-0.5">*</span>
                                    </Label>
                                    {renderParticipantSelect({
                                        value: singlePayeeId,
                                        onChange: (v) => { setSinglePayeeId(v); clearError('payee'); },
                                        disabledIds: multiRows.filter(r => r.id).map(r => ({ id: r.id, reason: 'Already selected as Payer' })),
                                        placeholder: 'Who receives all payments?',
                                        hasError: !!errors.payee,
                                    })}
                                    {errors.payee && <p className="text-xs text-red-500 mt-1">{errors.payee}</p>}
                                </div>

                                {/* Payer rows */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400" />
                                        Payers
                                    </Label>
                                    {multiRows.map((row, idx) => (
                                        <div key={idx} className="flex items-center gap-2 border rounded-lg p-2 bg-muted/20">
                                            <div className="flex-1">
                                                {renderParticipantSelect({
                                                    value: row.id,
                                                    onChange: (v) => updateRow(idx, 'id', v),
                                                    disabledIds: [
                                                        ...(singlePayeeId ? [{ id: singlePayeeId, reason: 'Already selected as Payee' }] : []),
                                                        ...multiRows.filter((r, i) => r.id && i !== idx).map(r => ({ id: r.id, reason: 'Already selected as Payer' }))
                                                    ],
                                                    placeholder: `Payer ${idx + 1}`,
                                                })}
                                            </div>
                                            <div className="w-28">
                                                <Input
                                                    type="number" min="0.01" step="0.01"
                                                    placeholder="Amount"
                                                    value={row.amount}
                                                    onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex-1 hidden sm:block">
                                                <Input
                                                    placeholder="Description (optional)"
                                                    value={row.description}
                                                    onChange={(e) => updateRow(idx, 'description', e.target.value)}
                                                />
                                            </div>
                                            {multiRows.length > 1 && (
                                                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(idx)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addRow}
                                        className="w-full"
                                        disabled={multiRows.length >= participants.length - 1}
                                    >
                                        <PlusCircle className="h-4 w-4 mr-1.5" /> Add Payer
                                    </Button>
                                    {errors.multiRows && (
                                        <p className="text-xs text-red-500 mt-1">{errors.multiRows}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── MULTI-RECEIVER MODE ── */}
                        {mode === 'multi_receiver' && (
                            <div className="space-y-4">
                                {/* Fixed Payer */}
                                <div className="space-y-1">
                                    <Label className="flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400" />
                                        Payer (funds all escrows)
                                        <span className="text-red-500 ml-0.5">*</span>
                                    </Label>
                                    {renderParticipantSelect({
                                        value: singlePayerId,
                                        onChange: (v) => { setSinglePayerId(v); clearError('payer'); },
                                        disabledIds: multiRows.filter(r => r.id).map(r => ({ id: r.id, reason: 'Already selected as Payee' })),
                                        placeholder: 'Who pays for all?',
                                        hasError: !!errors.payer,
                                    })}
                                    {errors.payer && <p className="text-xs text-red-500 mt-1">{errors.payer}</p>}
                                </div>

                                {/* Payee rows */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        Payees
                                    </Label>
                                    {multiRows.map((row, idx) => (
                                        <div key={idx} className="flex items-center gap-2 border rounded-lg p-2 bg-muted/20">
                                            <div className="flex-1">
                                                {renderParticipantSelect({
                                                    value: row.id,
                                                    onChange: (v) => updateRow(idx, 'id', v),
                                                    disabledIds: [
                                                        ...(singlePayerId ? [{ id: singlePayerId, reason: 'Already selected as Payer' }] : []),
                                                        ...multiRows.filter((r, i) => r.id && i !== idx).map(r => ({ id: r.id, reason: 'Already selected as Payee' }))
                                                    ],
                                                    placeholder: `Payee ${idx + 1}`,
                                                })}
                                            </div>
                                            <div className="w-28">
                                                <Input
                                                    type="number" min="0.01" step="0.01"
                                                    placeholder="Amount"
                                                    value={row.amount}
                                                    onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex-1 hidden sm:block">
                                                <Input
                                                    placeholder="Description (optional)"
                                                    value={row.description}
                                                    onChange={(e) => updateRow(idx, 'description', e.target.value)}
                                                />
                                            </div>
                                            {multiRows.length > 1 && (
                                                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(idx)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addRow}
                                        className="w-full"
                                        disabled={multiRows.length >= participants.length - 1}
                                    >
                                        <PlusCircle className="h-4 w-4 mr-1.5" /> Add Payee
                                    </Button>
                                    {errors.multiRows && (
                                        <p className="text-xs text-red-500 mt-1">{errors.multiRows}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Multi total & deal value warning ── */}
                        {mode !== 'single' && multiRows.length > 0 && (
                            <div className="flex items-center justify-between px-3 py-2 rounded-md bg-muted text-sm">
                                <span className="text-muted-foreground">
                                    {multiRows.length} invoice{multiRows.length !== 1 ? 's' : ''} will be generated
                                </span>
                                <div className="flex items-center gap-2">
                                    {dealValue && Math.abs(multiTotal - dealValue) > 0.01 && (
                                        <Badge variant="outline" className="text-amber-600 border-amber-400 bg-amber-50">
                                            Deal value: {currency} {dealValue.toFixed(2)}
                                        </Badge>
                                    )}
                                    <span className="font-semibold">
                                        Total: {currency} {multiTotal.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* ── Notes ── */}
                        <div className="space-y-1">
                            <Label>Notes <span className="text-muted-foreground text-xs">(optional)</span></Label>
                            <Textarea
                                placeholder="Any additional notes for the invoice(s)..."
                                rows={2}
                                value={notes}
                                maxLength={NOTES_MAX}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                            <p className={`text-xs text-right mt-0.5 ${notes.length >= NOTES_MAX ? 'text-red-500' : 'text-muted-foreground'}`}>
                                {notes.length}/{NOTES_MAX}
                            </p>
                        </div>

                        {/* ── Terms notice ── */}
                        <p className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                            💡 Payment will be held in escrow on our platform. Funds are only released to the payee after the payer's explicit approval or after the auto-release window expires.
                        </p>
                    </div>
                </div>

                {/* ── Sticky Footer ── */}
                <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2">
                    <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        {loading ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
                        ) : (
                            <>
                                <FileText className="h-4 w-4 mr-2" />
                                {mode === 'single' ? 'Create Invoice' : `Create ${multiRows.length} Invoice${multiRows.length !== 1 ? 's' : ''}`}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
